import { Country } from '$/lib/Address';
import { Mixin }   from '$/lib/utils';

import { JSONable, Field }           from '$/entities/lib/JSONable';
import Permissions                   from '$/entities/lib/Permissions';
import { CommonEntity, ChildEntity } from '$/entities/BaseEntity';
import { DocumentType, File }        from '$/entities/File';
import { RolePermission }            from '$/entities/roles/RolePermission';

@Mixin(JSONable)
export class LandlordAssistProperties {

	/**
	 * The country to which the file applies.
	 * '' = all countries.
	 */
	@Field()
	country: Country = '' as Country;

	/**
	 * The region (state/province) to which the file applies.
	 * '*' = all regions.
	 */
	@Field()
	region: string = '*';

}

@CommonEntity() @ChildEntity()
@Permissions({
	write : [ Permissions.roleHasPermission(RolePermission.LandlordAssistFilesWrite), Permissions.stopChecks ],
})
export class LandlordAssistFile extends File {

	documentType = DocumentType.LandlordAssistReference;

	/**
	 * Properties which specify the country and region to which the file applies.
	 */
	@Permissions({ write : Permissions.serverOnly })
	properties: LandlordAssistProperties = new LandlordAssistProperties();

	constructor(initialValues?: Partial<LandlordAssistFile>) {
		if (initialValues?.name) {
			initialValues.name = LandlordAssistFile.normalizeFilename(initialValues.name);
		}
		super(initialValues);
		this.properties = initialValues?.properties ?? new LandlordAssistProperties();
	}

	/**
	 * Cleans up filenames for Landlord Assist purposes.
	 */
	static normalizeFilename(filename: string | File): string {
		filename = filename instanceof File ? filename.name : filename;

		// Some files have a . in the name, so split the name/extension this way instead of using split('.')
		const extensionIndex = filename.lastIndexOf('.');
		const name           = filename.slice(0, extensionIndex).replace(/\(\d+\)/g, ''); // Remove any (1) or similar from the name
		const extension      = filename.slice(extensionIndex + 1);

		// Sometimes dashes are used semantically in the name (e.g. "Ontario - Example File Name"), we want to preserve these
		// But if they're used in place of spaces (e.g. "example-file-name"), we want to replace them with spaces (startCase will do this)
		// Some files also have underscores or em dashes used in the same way, so we normalize them into regular dashes
		const nameParts = name.replace(/[_–]/g, '-').split(/\s*-\s+/).map(betterStartCase);

		return `${nameParts.join(' - ').trim()}.${extension}`;

		/**
		 * The built in lodash startCase function doesn't handle things like N5 well, it tries to split it into N 5
		 * This function handles that case better and otherwise functions pretty much the same
		 */
		function betterStartCase(str: string) {
			return str
				.split(/[^\p{Letter}\d]+/u) // splitting on non-(letter/digit) characters, which includes punctuation marks so those get stripped out
				.map(s => s === s.toUpperCase() ? s : s[0].toUpperCase() + s.slice(1).toLowerCase()) // capitalize the first letter of each word
				.join(' '); // put it back together with spaces
		}
	}

}
